import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Beef, Leaf, Wheat } from "lucide-react";

const HERO_IMG = "/images/hero-angus.jpeg";

const lines = [
  { text: "Angus beef by the", bold: false },
  { text: "Quarter.", bold: true },
  { text: "Half.", bold: true },
  { text: "Whole.", bold: true },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10 scale-110">
        <img src={HERO_IMG} alt="Cattle grazing on green pasture at golden hour" className="w-full h-full object-cover object-[35%_60%] md:object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/20" />
      </motion.div>

      <div className="w-full max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="text-cream font-bold text-2xl sm:text-3xl lg:text-4xl tracking-[0.15em] uppercase mb-3"
          data-testid="hero-overline"
        >
          Farm to Table<span className="text-clay">.</span>
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="text-cream/90 text-xs tracking-[0.25em] uppercase font-bold mb-8"
          data-testid="hero-tagline"
        >
          Local Florida Beef &middot; Serving Jacksonville &amp; Surrounding Counties
        </motion.p>

        <h1 className="font-display text-cream text-6xl sm:text-7xl lg:text-8xl tracking-tighter leading-[1.05] max-w-4xl" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pt-2 pb-2 -mt-2 -mb-1">
              <motion.span
                className={`block ${line.bold ? "font-bold" : "font-medium italic"}`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.0 }}
          className="text-cream/85 text-base sm:text-lg leading-relaxed max-w-xl mt-8"
          data-testid="hero-subcopy"
        >
          Farm-fresh Angus beef you can trust — raised and finished on a family farm
          in Palatka. Shares of the whole animal only — no individual cuts.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="flex flex-wrap gap-x-10 gap-y-4 mt-12 text-cream/80 text-sm"
        >
          <span className="flex items-center gap-2" data-testid="hero-badge-breed">
            <Beef className="w-4 h-4" strokeWidth={1.5} /> Angus &amp; Angus Cross
          </span>
          <span className="flex items-center gap-2" data-testid="hero-badge-grass">
            <Leaf className="w-4 h-4" strokeWidth={1.5} /> Bred, born &amp; raised in Florida
          </span>
          <span className="flex items-center gap-2" data-testid="hero-badge-grain">
            <Wheat className="w-4 h-4" strokeWidth={1.5} /> Grass fed, natural grain finish
          </span>
          <span className="flex items-center gap-2" data-testid="hero-badge-grassfinished">
            <Leaf className="w-4 h-4" strokeWidth={1.5} /> Grass fed, grass finished by request
          </span>
        </motion.div>
      </div>
    </section>
  );
};
